import React from 'react';
import { useTranslation } from 'react-i18next';
import { localizeClassItem } from '../../config/classUiConfig';
import './ActivityLogSection.css';

const ActivityLogSection = ({ activities }) => {
    const { t, i18n } = useTranslation('profile');
    const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';

    const getActivityIcon = (action) => {
        const icons = {
            profile_update: 'fa-user-edit',
            enrollment: 'fa-book',
            payment_method_added: 'fa-credit-card',
            certificate_earned: 'fa-certificate',
            default: 'fa-circle'
        };
        return icons[action] || icons.default;
    };

    const getLocalizedClassName = (name) => {
        if (!name) return name;
        return localizeClassItem({ title: name }, i18n.language).title;
    };

    const formatActivityText = (activity) => {
        switch (activity.action) {
            case 'profile_update':
                return t('activity.profileUpdate');
            case 'enrollment':
                return t('activity.enrolledIn', {
                    className: getLocalizedClassName(activity.details.class_name)
                });
            case 'payment_method_added':
                return t('activity.paymentAdded', { lastFour: activity.details.last_four });
            case 'certificate_earned':
                return t('activity.certificateEarned', { name: activity.details.certificate_name });
            default:
                return activity.action.replace(/_/g, ' ');
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return t('activity.today');
        if (diffDays === 1) return t('activity.yesterday');
        if (diffDays < 7) return t('activity.daysAgo', { count: diffDays });
        return date.toLocaleDateString(dateLocale);
    };

    const safeActivities = activities || [];
    const groupedActivities = safeActivities.reduce((groups, activity) => {
        const date = new Date(activity.created_at).toLocaleDateString(dateLocale);
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(activity);
        return groups;
    }, {});

    return (
        <div className="activity-log-section">
            <div className="section-header">
                <h2>{t('activity.title')}</h2>
            </div>

            {safeActivities.length > 0 ? (
                <div className="activity-timeline">
                    {Object.entries(groupedActivities).map(([date, dateActivities]) => (
                        <div key={date} className="activity-date-group">
                            <div className="activity-date-header">
                                {formatDate(dateActivities[0].created_at)}
                            </div>
                            {dateActivities.map(activity => (
                                <div key={activity.id} className="activity-item">
                                    <div className="activity-icon">
                                        <i className={`fas ${getActivityIcon(activity.action)}`}></i>
                                    </div>
                                    <div className="activity-content">
                                        <div className="activity-text">
                                            {formatActivityText(activity)}
                                        </div>
                                        <div className="activity-time">
                                            {new Date(activity.created_at).toLocaleTimeString(dateLocale, {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="empty-state">
                    <i className="fas fa-history"></i>
                    <p>{t('activity.emptyTitle')}</p>
                    <p className="empty-state-subtext">
                        {t('activity.emptyDesc')}
                    </p>
                </div>
            )}
        </div>
    );
};

export default ActivityLogSection;
